<template>
  <v-container>
    <v-row>
      <!-- Category Performance Insights -->
      <v-col
        cols="12"
        md="6"
      >
        <v-card class="pa-4 mb-4">
          <h4 class="text-h6 mb-3">
            <v-icon
              color="success"
              class="mr-2"
            >
              mdi-trending-up
            </v-icon>
            Rendimiento de Categorías
          </h4>
          <v-list>
            <v-list-item
              v-for="insight in performanceInsights"
              :key="insight.id"
            >
              <v-list-item-avatar>
                <v-icon :color="insight.color">
                  {{ insight.icon }}
                </v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="text-body-2">
                  {{ insight.title }}
                </v-list-item-title>
                <v-list-item-subtitle>{{ insight.description }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action v-if="insight.value">
                <v-chip
                  :color="insight.color"
                  text-color="white"
                  small
                >
                  {{ insight.value }}
                </v-chip>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>

      <!-- Revenue Distribution Analysis -->
      <v-col
        cols="12"
        md="6"
      >
        <v-card class="pa-4 mb-4">
          <h4 class="text-h6 mb-3">
            <v-icon
              color="info"
              class="mr-2"
            >
              mdi-chart-pie
            </v-icon>
            Distribución de Ingresos
          </h4>
          <div
            v-for="category in revenueDistribution"
            :key="category.name"
            class="mb-3"
          >
            <div class="d-flex justify-space-between align-center mb-1">
              <span class="text-body-2">{{ category.name }}</span>
              <span class="text-body-2 font-weight-bold">${{ category.revenue.toLocaleString() }}</span>
            </div>
            <v-progress-linear
              :value="category.percentage"
              :color="category.color"
              height="8"
              rounded
            >
              <template v-slot:default="{ value }">
                <small class="white--text">{{ Math.ceil(value) }}%</small>
              </template>
            </v-progress-linear>
          </div>
        </v-card>
      </v-col>

      <!-- Recommendations -->
      <v-col
        cols="12"
        md="6"
      >
        <v-card class="pa-4 mb-4">
          <h4 class="text-h6 mb-3">
            <v-icon
              color="warning"
              class="mr-2"
            >
              mdi-lightbulb-on
            </v-icon>
            Recomendaciones
          </h4>
          <v-list>
            <v-list-item
              v-for="recommendation in recommendations"
              :key="recommendation.id"
            >
              <v-list-item-avatar>
                <v-icon
                  :color="recommendation.priority"
                  size="20"
                >
                  {{ recommendation.icon }}
                </v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="text-body-2">
                  {{ recommendation.title }}
                </v-list-item-title>
                <v-list-item-subtitle class="text-caption">
                  {{ recommendation.description }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn
                  text
                  small
                  :color="recommendation.priority"
                  @click="openRecommendationDetails(recommendation)"
                >
                  Ver más
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>

      <!-- Category Trends -->
      <v-col
        cols="12"
        md="6"
      >
        <v-card class="pa-4 mb-4">
          <h4 class="text-h6 mb-3">
            <v-icon
              color="purple"
              class="mr-2"
            >
              mdi-chart-timeline
            </v-icon>
            Tendencias y Alertas
          </h4>
          <v-list>
            <v-list-item
              v-for="trend in categoryTrends"
              :key="trend.id"
            >
              <v-list-item-avatar>
                <v-icon :color="trend.type === 'positive' ? 'success' : trend.type === 'negative' ? 'error' : 'warning'">
                  {{ trend.icon }}
                </v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="text-body-2">
                  {{ trend.category }}
                </v-list-item-title>
                <v-list-item-subtitle>{{ trend.description }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-chip
                  :color="trend.type === 'positive' ? 'success' : trend.type === 'negative' ? 'error' : 'warning'"
                  text-color="white"
                  small
                >
                  {{ trend.change }}
                </v-chip>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>

      <!-- Advanced Metrics -->
      <v-col cols="12">
        <v-card class="pa-4">
          <h4 class="text-h6 mb-3">
            <v-icon
              color="deep-purple"
              class="mr-2"
            >
              mdi-google-analytics
            </v-icon>
            Métricas Avanzadas
          </h4>
          <v-row>
            <v-col
              v-for="metric in advancedMetrics"
              :key="metric.name"
              cols="12"
              sm="6"
              md="3"
            >
              <v-card
                outlined
                class="pa-3 text-center"
              >
                <v-icon
                  :color="metric.color"
                  size="32"
                  class="mb-2"
                >
                  {{ metric.icon }}
                </v-icon>
                <div class="text-h6 font-weight-bold">
                  {{ metric.value }}
                </div>
                <div class="text-body-2 grey--text">
                  {{ metric.name }}
                </div>
                <div
                  class="text-caption"
                  :class="metric.changeColor + '--text'"
                >
                  {{ metric.change }}
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <!-- Recommendation Details Dialog -->
    <v-dialog
      v-model="recommendationDialog"
      max-width="600"
    >
      <v-card v-if="selectedRecommendation">
        <v-card-title>
          <v-icon
            :color="selectedRecommendation.priority"
            class="mr-2"
          >
            {{ selectedRecommendation.icon }}
          </v-icon>
          {{ selectedRecommendation.title }}
        </v-card-title>
        <v-card-text>
          <p>{{ selectedRecommendation.description }}</p>
          <div v-if="selectedRecommendation.details">
            <h4 class="text-h6 mt-4 mb-2">
              Detalles:
            </h4>
            <ul>
              <li
                v-for="detail in selectedRecommendation.details"
                :key="detail"
              >
                {{ detail }}
              </li>
            </ul>
          </div>
          <div v-if="selectedRecommendation.actions">
            <h4 class="text-h6 mt-4 mb-2">
              Acciones recomendadas:
            </h4>
            <v-chip-group column>
              <v-chip
                v-for="action in selectedRecommendation.actions"
                :key="action"
                small
                outlined
              >
                {{ action }}
              </v-chip>
            </v-chip-group>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="recommendationDialog = false"
          >
            Cerrar
          </v-btn>
          <v-btn
            color="primary"
            @click="implementRecommendation"
          >
            Implementar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
  export default {
    name: 'CategoryInsights',
    props: {
      categoryData: {
        type: Object,
        default: () => ({}),
      },
      timeRange: {
        type: String,
        default: 'daily',
      },
    },
    data () {
      return {
        recommendationDialog: false,
        selectedRecommendation: null,
      }
    },
    computed: {
      performanceInsights () {
        const insights = []
        const data = this.timeRange === 'daily' ? this.categoryData.daily : this.categoryData.monthly

        if (!data || Object.keys(data).length === 0) {
          return [{
            id: 1,
            title: 'Sin datos disponibles',
            description: 'No hay suficientes datos para generar insights',
            icon: 'mdi-information',
            color: 'grey',
          }]
        }

        // Calculate top performing category
        const categoryTotals = this.calculateCategoryTotals(data)
        const topCategory = categoryTotals.length > 0 ? categoryTotals[0] : null

        if (topCategory) {
          insights.push({
            id: 1,
            title: 'Categoría más rentable',
            description: `${topCategory.name} genera el mayor ingreso`,
            icon: 'mdi-trophy',
            color: 'success',
            value: `$${topCategory.revenue.toLocaleString()}`,
          })
        }

        // Growth trend insight
        insights.push({
          id: 2,
          title: 'Tendencia de crecimiento',
          description: 'Las ventas muestran tendencia positiva',
          icon: 'mdi-trending-up',
          color: 'success',
          value: '+12%',
        })

        // Category diversity
        insights.push({
          id: 3,
          title: 'Diversificación',
          description: `${categoryTotals.length} categorías activas`,
          icon: 'mdi-view-grid',
          color: 'info',
          value: categoryTotals.length > 5 ? 'Buena' : 'Limitada',
        })

        return insights
      },
      revenueDistribution () {
        const data = this.timeRange === 'daily' ? this.categoryData.daily : this.categoryData.monthly
        const categoryTotals = this.calculateCategoryTotals(data)
        const totalRevenue = categoryTotals.reduce((sum, cat) => sum + cat.revenue, 0)
        const colors = ['success', 'info', 'warning', 'error', 'purple']

        return categoryTotals.slice(0, 5).map((category, index) => ({
          ...category,
          percentage: totalRevenue > 0 ? (category.revenue / totalRevenue) * 100 : 0,
          color: colors[index % colors.length],
        }))
      },
      recommendations () {
        return [
          {
            id: 1,
            title: 'Optimizar inventario',
            description: 'Ajustar stock de categorías de alta rotación',
            icon: 'mdi-package-variant',
            priority: 'warning',
            details: [
              'Incrementar stock de productos de alta demanda',
              'Reducir inventario de productos de baja rotación',
              'Implementar reposición automática',
            ],
            actions: ['Revisar stock', 'Configurar alertas', 'Automatizar pedidos'],
          },
          {
            id: 2,
            title: 'Promocionar categorías emergentes',
            description: 'Impulsar ventas en categorías con potencial',
            icon: 'mdi-rocket-launch',
            priority: 'success',
            details: [
              'Crear campañas promocionales específicas',
              'Ofrecer descuentos estratégicos',
              'Mejorar visibilidad en el punto de venta',
            ],
            actions: ['Crear promociones', 'Diseñar campañas', 'Analizar competencia'],
          },
          {
            id: 3,
            title: 'Revisar precios',
            description: 'Análisis de margen en categorías principales',
            icon: 'mdi-currency-usd',
            priority: 'info',
            details: [
              'Comparar precios con la competencia',
              'Evaluar elasticidad de la demanda',
              'Optimizar márgenes de ganancia',
            ],
            actions: ['Estudio de mercado', 'Ajustar precios', 'Monitorear impacto'],
          },
        ]
      },
      categoryTrends () {
        return [
          {
            id: 1,
            category: 'Electrónicos',
            description: 'Crecimiento sostenido en ventas',
            type: 'positive',
            icon: 'mdi-trending-up',
            change: '+15%',
          },
          {
            id: 2,
            category: 'Ropa',
            description: 'Disminución en ventas recientes',
            type: 'negative',
            icon: 'mdi-trending-down',
            change: '-8%',
          },
          {
            id: 3,
            category: 'Hogar',
            description: 'Ventas estables con picos estacionales',
            type: 'neutral',
            icon: 'mdi-minus',
            change: '+2%',
          },
        ]
      },
      advancedMetrics () {
        return [
          {
            name: 'Rotación de Inventario',
            value: '4.2x',
            icon: 'mdi-refresh',
            color: 'success',
            change: '+0.3 vs período anterior',
            changeColor: 'success',
          },
          {
            name: 'Ticket Promedio',
            value: '$85',
            icon: 'mdi-receipt',
            color: 'info',
            change: '+$12 vs período anterior',
            changeColor: 'success',
          },
          {
            name: 'Margen Promedio',
            value: '23%',
            icon: 'mdi-percent',
            color: 'warning',
            change: '-1.2% vs período anterior',
            changeColor: 'error',
          },
          {
            name: 'Conversión',
            value: '67%',
            icon: 'mdi-target',
            color: 'purple',
            change: '+5% vs período anterior',
            changeColor: 'success',
          },
        ]
      },
    },
    methods: {
      calculateCategoryTotals (data) {
        if (!data || Object.keys(data).length === 0) return []

        const categoryTotals = {}
        Object.keys(data).forEach(date => {
          const dateData = data[date]
          Object.keys(dateData).forEach(categoryId => {
            const categoryData = dateData[categoryId]
            const categoryName = categoryData.categoryName || `Categoría ${categoryId}`
            if (!categoryTotals[categoryName]) {
              categoryTotals[categoryName] = {
                name: categoryName,
                revenue: 0,
                transactions: 0,
                quantity: 0,
              }
            }
            categoryTotals[categoryName].revenue += categoryData.totalRevenue || 0
            categoryTotals[categoryName].transactions += categoryData.transactionCount || 0
            categoryTotals[categoryName].quantity += categoryData.totalQuantity || 0
          })
        })

        return Object.values(categoryTotals)
          .sort((a, b) => b.revenue - a.revenue)
      },
      openRecommendationDetails (recommendation) {
        this.selectedRecommendation = recommendation
        this.recommendationDialog = true
      },
      implementRecommendation () {
        // Here you would implement the recommendation logic
        this.$emit('implement-recommendation', this.selectedRecommendation)
        this.recommendationDialog = false

        // Show success message
        this.$emit('show-message', {
          type: 'success',
          message: `Recomendación "${this.selectedRecommendation.title}" marcada para implementación`,
        })
      },
    },
  }
</script>

<style scoped>
.v-list-item__subtitle {
  white-space: normal !important;
}
</style>
